import { useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Target } from 'lucide-react';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { SafeChart } from '@/components/passport/SafeChart';
import {
  getCategoryIcon,
  getCategoryVerdict,
  getVerdictColor,
  getSkillBarColor,
  getWeightedScore,
  CATEGORY_GROUPS,
  type ProficiencyData
} from '@/utils/technicianPassportUtils';
import { useSoftProficiencies } from '@/hooks/useSoftProficiency';
import { PassportProvenanceBadge } from '@/components/passport/PassportProvenanceBadge';
import {
  PORTABLE_CLASS_LABEL,
  PORTABLE_CLASS_MARK,
  proficiencyFactGroup,
} from '@/lib/passport/proficiencyProvenance';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PassportProficiencySectionProps {
  proficiencies: ProficiencyData[];
  /**
   * The proficiency read FAILED. See PassportEmploymentHistory for why this is
   * a separate signal from an empty array: CORE-C must not report its own
   * retrieval failure as a fact about the person's competence.
   */
  isError?: boolean;
  technicianProfileId?: string;
}

export function PassportProficiencySection({
  proficiencies,
  technicianProfileId,
  isError = false,
}: PassportProficiencySectionProps) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    'Mechanical Systems': true,
    'Electrical & Diagnostics': true,
    'Chassis Systems': false,
    'Maintenance & Inspection': false,
  });
  
  // Fetch soft proficiencies for training boost display
  const { data: softProficiencies } = useSoftProficiencies(technicianProfileId);

  // AFTER every hook — an early return above them would violate the rules of
  // hooks. A failed proficiency read must not be drawn as a competence profile
  // computed from nothing.
  if (isError) {
    return <PassportSectionFallback sectionName="Proficiencies" />;
  }
  
  // Helper to get soft proficiency for a category
  const getSoftProficiency = (categoryKey: string) => {
    return softProficiencies?.find(sp => sp.category_key === categoryKey)?.soft_score || 0;
  };

  // Prepare radar chart data
  // CORE-C GAP-007. Inclusion requires a KNOWN sample size — `null >= 2` is
  // false in JS, but relying on that coercion is how the defect stayed
  // invisible, so it is explicit. The plotted value is the confidence-WEIGHTED
  // score, so an entry whose confidence is unknown has no weighted score to
  // plot and is left out rather than plotted at its raw value under the same
  // axis label.
  const radarData = proficiencies
    .filter(p => p.sample_size !== null && p.sample_size >= 2)
    .map(p => ({
      category: p.category_key.length > 12 ? p.category_key.slice(0, 10) + '...' : p.category_key,
      fullName: p.category_key,
      score: getWeightedScore(p.proficiency_score, p.confidence_score),
      rawScore: p.proficiency_score,
      confidence: p.confidence_score === null ? null : Math.round(p.confidence_score * 100),
    }))
    .filter(d => d.score !== null)
    .slice(0, 8); // Limit to 8 for readability

  const toggleGroup = (group: string) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const getProficiencyForCategory = (category: string) => {
    return proficiencies.find(p => p.category_key === category);
  };

  return (
    <Card data-testid="passport-proficiencies">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-muted-foreground" />
          Category Proficiencies
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Radar Chart */}
        {radarData.length >= 3 && (
          <div className="w-full h-[320px] bg-muted/30 rounded-xl p-4">
            <SafeChart height="320px">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  />
                  <PolarRadiusAxis 
                    angle={30} 
                    domain={[0, 100]} 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  />
                  <Radar
                    name="Weighted Score"
                    dataKey="score"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                    formatter={(value: number, name: string, props: any) => [
                      props.payload.confidence === null
                        ? `${props.payload.rawScore}%`
                        : `${props.payload.rawScore}% (${props.payload.confidence}% conf)`,
                      props.payload.fullName
                    ]}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </SafeChart>
          </div>
        )}

        {/* Category Groups */}
        <div className="space-y-4">
          {Object.entries(CATEGORY_GROUPS).map(([groupName, categories]) => (
            <Collapsible 
              key={groupName} 
              open={openGroups[groupName]}
              onOpenChange={() => toggleGroup(groupName)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                <span className="font-semibold text-foreground">{groupName}</span>
                {openGroups[groupName] ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-2">
                  {categories.map(category => {
                    const prof = getProficiencyForCategory(category);
                    const Icon = getCategoryIcon(category);
                    // CORE-C GAP-007. `|| 0` here recreated the fabrication
                    // downstream of the adapters, and additionally turned a
                    // real 0 and an unknown into the same thing. `??` keeps a
                    // real zero and preserves null as null. The score keeps its
                    // `|| 0` for the genuinely absent-category case: there is no
                    // proficiency row at all there, which the surrounding UI
                    // already treats as an unrated category.
                    const score = prof?.proficiency_score || 0;
                    const confidence = prof?.confidence_score ?? null;
                    const sampleSize = prof?.sample_size ?? null;
                    const verdict = getCategoryVerdict(score, confidence, sampleSize);
                    // Core-100 R5 (CORE-C F/Prov): a manager's opening baseline and a demonstrated value must not
                    // look identical. The class is stated in words on every value whose class is known; the
                    // approved "Calculated by OptiBay" badge is added where the class is a derivation. An absent
                    // row, or a class that could not be read, states nothing.
                    const provenanceClass = prof ? prof.provenance_class ?? null : null;
                    const provenanceFact = proficiencyFactGroup(provenanceClass);

                    return (
                      <div 
                        key={category}
                        className="p-4 bg-card border border-border rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium text-sm text-foreground">{category}</span>
                          </div>
                          {verdict !== null && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${getVerdictColor(verdict)}`}
                            >
                              {verdict}
                            </Badge>
                          )}
                        </div>

                        {provenanceClass && (
                          <div
                            className="flex flex-wrap items-center gap-2 mb-2"
                            data-testid={`proficiency-class-${category}`}
                            data-class={provenanceClass}
                          >
                            <span
                              className="text-[11px] font-medium text-muted-foreground"
                              title={PORTABLE_CLASS_LABEL[provenanceClass]}
                            >
                              {PORTABLE_CLASS_MARK[provenanceClass]}
                            </span>
                            {provenanceFact && <PassportProvenanceBadge factGroupId={provenanceFact} />}
                          </div>
                        )}

                        {/* Skill Bar with Soft Proficiency Overlay */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Score</span>
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-medium text-foreground cursor-help">
                                    {score}%
                                    {getSoftProficiency(category) > 0 && (
                                      <span className="text-primary/60 ml-1">
                                        (+{getSoftProficiency(category).toFixed(1)}%)
                                      </span>
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  <p>Real proficiency: {score}%</p>
                                  {getSoftProficiency(category) > 0 && (
                                    <p className="text-primary">Training boost: +{getSoftProficiency(category).toFixed(1)}%</p>
                                  )}
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden relative">
                            {/* Real proficiency bar (solid) */}
                            <div 
                              className={`h-full ${getSkillBarColor(score)} transition-all duration-500`}
                              style={{ width: `${score}%` }}
                            />
                            {/* Soft proficiency overlay (dashed pattern) */}
                            {getSoftProficiency(category) > 0 && (
                              <div 
                                className="absolute top-0 h-full bg-primary/30 transition-all duration-500"
                                style={{ 
                                  left: `${score}%`,
                                  width: `${Math.min(getSoftProficiency(category), 100 - score)}%`,
                                  backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, hsl(var(--primary) / 0.3) 2px, hsl(var(--primary) / 0.3) 4px)',
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* CORE-C GAP-007. These two lines rendered
                            "Confidence: 50%" and "0 jobs" from invented
                            metadata. Each is now shown only when CORE-C
                            actually holds the value; absence of metadata is
                            presented as absence, with no substitute copy. A
                            real 0 still renders as 0% / 0 jobs. */}
                        {(confidence !== null || sampleSize !== null) && (
                          <div className="flex justify-between mt-3 text-xs text-muted-foreground">
                            {confidence !== null && (
                              <span>Confidence: {Math.round(confidence * 100)}%</span>
                            )}
                            {sampleSize !== null && <span>{sampleSize} jobs</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
